import React, { useCallback, useMemo } from 'react';

import { Box } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import Filters from 'components/Filters';
import InputWithDebouncedAction from 'components/InputWithDebouncedAction/InputWithDebouncedAction';
import type { OwnerWorkspaceFilters } from 'config/api/assetRopa/assetRopaFilter.types';
import type { OwnerWorkspaceFilterOptions } from 'config/api/assetRopa/assetRopaWorkspace.helpers';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import {
  buildOwnerWorkspaceAvailableFilters,
  mergeTableFiltersIntoOwnerWorkspaceFilters,
  ownerWorkspaceFilterValuesToTableFilters,
  type OwnerWorkspaceTableFilterState,
} from '../ownerWorkspaceDrawerFilters';

type Props = {
  filterOptions: OwnerWorkspaceFilterOptions;
  filters: OwnerWorkspaceFilters;
  onFiltersChange: (filters: OwnerWorkspaceFilters) => void;
};

const OwnerWorkspaceFiltersToolbar: React.FC<Props> = ({ filterOptions, filters, onFiltersChange }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.owner_workspace.filters;

  const availableTableFilters = useMemo(() => buildOwnerWorkspaceAvailableFilters(filterOptions, filters), [filterOptions, filters]);

  const tableFilters = useMemo(() => ownerWorkspaceFilterValuesToTableFilters(filters, filterOptions), [filterOptions, filters]);

  const setTableFilters = useCallback(
    (updater: React.SetStateAction<OwnerWorkspaceTableFilterState>) => {
      const currentTableFilters = ownerWorkspaceFilterValuesToTableFilters(filters, filterOptions);
      const nextTableFilters = typeof updater === 'function' ? updater(currentTableFilters) : updater;
      onFiltersChange(mergeTableFiltersIntoOwnerWorkspaceFilters(filters, nextTableFilters));
    },
    [filterOptions, filters, onFiltersChange],
  );

  const updateSearch = (value: string) => {
    onFiltersChange({ ...filters, search: value });
  };

  return (
    <Box className={styles.toolbarSection}>
      <Box className={styles.ownerWorkspaceSearchRow}>
        <div className={styles.ownerWorkspaceSearchInputCell}>
          <InputWithDebouncedAction
            action={updateSearch}
            className={styles.searchInputLabelEllipsis}
            formControl={{ size: 'small' }}
            label={t(messages.search_placeholder)}
            value={filters.search}
          />
        </div>
        <Filters availableFilters={availableTableFilters} filters={tableFilters} setFilters={setTableFilters} />
      </Box>
    </Box>
  );
};

export default OwnerWorkspaceFiltersToolbar;
